import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronDown, FileDown, Loader2, Mail } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { usePageMeta } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "closed"];
const FILTERS = ["all", ...STATUSES];

const STATUS_STYLES = {
  new: "bg-[#B8997A]/15 text-[#8a6f52] border-[#B8997A]/50",
  contacted: "bg-[#0B0F14]/5 text-[#0B0F14] border-[#0B0F14]/30",
  closed: "bg-transparent text-muted-foreground border-border line-through decoration-1",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

const mailtoHref = (record, kind) => {
  const topic = kind === "inquiry" ? record.subject : record.area;
  const noun = kind === "inquiry" ? "inquiry" : "application";
  const subject = `Re: Your ${noun} to SKN Group — ${topic}`;
  const body = `Dear ${record.name},\n\nThank you for reaching out to SKN Group regarding "${topic}".\n\n\n\nWarm regards,\nSKN Group of Companies\n1, Ripon Street, Kolkata 700016, India\n1800 12000 1400 (toll-free)`;
  return `mailto:${record.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const StatusBadge = ({ record, kind, onChange }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        data-testid={`status-badge-${record.id}`}
        className={`inline-flex items-center gap-1.5 border px-3 py-1 text-xs uppercase tracking-wider transition-colors ${STATUS_STYLES[record.status] || STATUS_STYLES.new}`}
      >
        {record.status || "new"}
        <ChevronDown size={12} strokeWidth={1.5} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-[#F7F5F0] min-w-[140px]">
      {STATUSES.map((s) => (
        <DropdownMenuItem
          key={s}
          data-testid={`status-option-${record.id}-${s}`}
          onClick={() => onChange(kind, record.id, s)}
          className="text-xs uppercase tracking-wider cursor-pointer"
        >
          {s}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const FilterTabs = ({ records, active, setActive, testPrefix }) => {
  const count = (f) => (f === "all" ? records.length : records.filter((r) => (r.status || "new") === f).length);
  return (
    <div className="flex flex-wrap gap-2 mb-5" data-testid={`${testPrefix}-filters`}>
      {FILTERS.map((f) => (
        <button
          key={f}
          data-testid={`${testPrefix}-filter-${f}`}
          onClick={() => setActive(f)}
          className={`px-4 py-1.5 text-xs uppercase tracking-wider border transition-colors duration-300 ${
            active === f
              ? "bg-foreground text-background border-foreground"
              : "border-foreground/20 text-muted-foreground hover:border-[#B8997A] hover:text-[#B8997A]"
          }`}
        >
          {f} ({count(f)})
        </button>
      ))}
    </div>
  );
};

const ReplyButton = ({ record, kind, onChange }) => (
  <a
    href={mailtoHref(record, kind)}
    data-testid={`reply-link-${record.id}`}
    onClick={() => {
      if ((record.status || "new") === "new") onChange(kind, record.id, "contacted");
    }}
    className="inline-flex items-center gap-1.5 text-sm bronze hover:underline whitespace-nowrap"
  >
    <Mail size={14} strokeWidth={1.5} /> Reply
  </a>
);

export default function PortalInquiries() {
  usePageMeta("Inquiries Admin — SKN Group Portal", "Website inquiries and career applications.");
  const [inquiries, setInquiries] = useState(null);
  const [applications, setApplications] = useState(null);
  const [inqFilter, setInqFilter] = useState("all");
  const [appFilter, setAppFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [inq, apps] = await Promise.all([
        axios.get(`${API}/inquiries`, { withCredentials: true }),
        axios.get(`${API}/applications`, { withCredentials: true }),
      ]);
      setInquiries(inq.data);
      setApplications(apps.data);
    } catch (err) {
      console.error("Failed to load inquiries/applications", err);
      setInquiries([]);
      setApplications([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (kind, id, status) => {
    const [list, setList] =
      kind === "inquiry" ? [inquiries, setInquiries] : [applications, setApplications];
    const prev = list;
    setList(list.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await axios.patch(
        `${API}/${kind === "inquiry" ? "inquiries" : "applications"}/${id}/status`,
        { status },
        { withCredentials: true }
      );
    } catch {
      setList(prev);
      toast.error("Could not update status. Please try again.");
    }
  };

  if (inquiries === null || applications === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin bronze" />
      </div>
    );
  }

  const visibleInquiries =
    inqFilter === "all" ? inquiries : inquiries.filter((r) => (r.status || "new") === inqFilter);
  const visibleApplications =
    appFilter === "all" ? applications : applications.filter((r) => (r.status || "new") === appFilter);

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="portal-inquiries-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link
          to="/portal"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#B8997A] transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to Portal
        </Link>
        <h1 className="mt-6 font-display text-4xl md:text-5xl font-medium tracking-tight">
          Inquiries <span className="italic bronze">&amp; Applications</span>
        </h1>

        {/* Contact inquiries */}
        <section className="mt-14" data-testid="admin-inquiries-section">
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-5">
            Contact Inquiries ({inquiries.length})
          </h2>
          <FilterTabs records={inquiries} active={inqFilter} setActive={setInqFilter} testPrefix="inquiries" />
          <div className="border border-foreground/10 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead className="min-w-[240px]">Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleInquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                      No inquiries{inqFilter !== "all" ? ` with status "${inqFilter}"` : " yet"}.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleInquiries.map((q) => (
                    <TableRow key={q.id} data-testid={`inquiry-row-${q.id}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(q.created_at)}</TableCell>
                      <TableCell className="font-medium">{q.name}</TableCell>
                      <TableCell>{q.email}</TableCell>
                      <TableCell>{q.subject}</TableCell>
                      <TableCell className="text-muted-foreground">{q.message}</TableCell>
                      <TableCell>
                        <StatusBadge record={q} kind="inquiry" onChange={updateStatus} />
                      </TableCell>
                      <TableCell>
                        <ReplyButton record={q} kind="inquiry" onChange={updateStatus} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Career applications */}
        <section className="mt-16" data-testid="admin-applications-section">
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-5">
            Career Applications ({applications.length})
          </h2>
          <FilterTabs records={applications} active={appFilter} setActive={setAppFilter} testPrefix="applications" />
          <div className="border border-foreground/10 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead className="min-w-[200px]">Message</TableHead>
                  <TableHead>CV</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleApplications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-10">
                      No applications{appFilter !== "all" ? ` with status "${appFilter}"` : " yet"}.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleApplications.map((a) => (
                    <TableRow key={a.id} data-testid={`application-row-${a.id}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell>{a.email}</TableCell>
                      <TableCell>{a.area}</TableCell>
                      <TableCell className="text-muted-foreground">{a.message}</TableCell>
                      <TableCell>
                        {a.cv_storage_path ? (
                          <a
                            href={`${API}/applications/${a.id}/cv`}
                            data-testid={`cv-download-${a.id}`}
                            className="inline-flex items-center gap-1.5 text-sm bronze hover:underline whitespace-nowrap"
                          >
                            <FileDown size={14} strokeWidth={1.5} /> CV
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge record={a} kind="application" onChange={updateStatus} />
                      </TableCell>
                      <TableCell>
                        <ReplyButton record={a} kind="application" onChange={updateStatus} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>
    </div>
  );
}
